import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Box,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { previewPlaylist, getVenue } from "../api";
import readStreamAsBase64 from "../utils/readStreamAsBase64";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DownloadIcon from "@mui/icons-material/Download";
import CopyPlaylistModal from "./CopyPlaylistModal";
import { DataGrid } from "@mui/x-data-grid";
import AccessTime from "@mui/icons-material/AccessTime";
import Euro from "@mui/icons-material/Euro";
import Dollar from "@mui/icons-material/AttachMoney";
import currencies from "../utils/currencies";
import { getTimeInSelectedTimezone } from "../utils/dateConvert";
import { zonedTimeToUtc } from "date-fns-tz";
import Loader from "../components/common/Loader";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import tzLookup from "tz-lookup";
import PriceChangeModal from "./PriceChangeModal";
import LibrarySelectionModal from "./LibrarySelectionModal";

const newId = () =>
  `uid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const startHour = 12;
const endHour = 12;
const timeOptions = [];
for (let i = startHour; i < 24; i++) {
  for (let j = 0; j < 4; j++) {
    const time = `${i.toString().padStart(2, "0")}:${(15 * j)
      .toString()
      .padStart(2, "0")}`;
    timeOptions.push({
      label: time,
      value: time,
    });
  }
}
for (let i = 0; i < endHour; i++) {
  for (let j = 0; j < 4; j++) {
    const time = `${i.toString().padStart(2, "0")}:${(15 * j)
      .toString()
      .padStart(2, "0")}`;
    timeOptions.push({
      label: time,
      value: time,
    });
  }
}

const normalizePriceInput = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value).replace(",", ".").replace(/[^\d.]/g, "");
};

export default function PlaylistModal(props) {
  const isLibraryEditor = !!props.isLibraryEditor;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const currencySymbol = currencies[props.venueCurrency] || "€";
  const currency = props.venueCurrency || "EUR";
  const [selectedRows, setSelectedRows] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);
  const [isPriceChangeModalOpen, setIsPriceChangeModalOpen] = useState(false);

  const columns = [
    {
      field: "item",
      headerName: "Track",
      flex: isMobile ? 0.7 : 1,
      editable: true,
      cellClassName: "track-cell",
    },
    {
      field: "price",
      headerName: `Custom Price (${currencySymbol})`,
      type: "number",
      editable: true,
      width: isMobile ? undefined : 200,
      flex: isMobile ? 0.3 : undefined,
      align: "left",
      headerAlign: "left",
      cellClassName: "price-cell",
      renderCell: (params) => (
        <div style={{ width: "100%", textAlign: "left" }}>{params.value}</div>
      ),
    },
  ];

  const handleCellEditCommit = (e) => {
    setState((s) => {
      const newPlaylist = s.playlist.map((pItem) => {
        if (pItem.id === e.id) {
          if (e.field === "item") {
            return { ...pItem, item: e.value };
          } else if (e.field === "price") {
            // Store raw edited price as specificPrice, convert "Free" or empty to undefined
            const newSpecificPrice =
              e.value === "Free" ||
              e.value === "" ||
              e.value === null ||
              e.value === undefined
                ? undefined
                : e.value.toString();
            return { ...pItem, specificPrice: newSpecificPrice };
          }
        }
        return pItem;
      });
      return { ...s, playlist: newPlaylist };
    });
  };

  const handleSortModelChange = (e) => {
    if (!e[0]?.sort) return;
    setState((state) => {
      let rows = [...state.rows];
      rows.sort((a, b) => {
        if (e[0].field === "item") {
          if (e[0].sort === "asc") {
            return a[e[0].field].localeCompare(b[e[0].field]);
          } else if (e[0].sort === "desc") {
            return b[e[0].field].localeCompare(a[e[0].field]);
          }
        } else if (e[0].field === "price") {
          if (e[0].sort === "asc") {
            return (a[e[0].field] || 0) - (b[e[0].field] || 0);
          } else if (e[0].sort === "desc") {
            return (b[e[0].field] || 0) - (a[e[0].field] || 0);
          }
        }
        return 0;
      });
      return { ...state, rows };
    });
  };

  const setTimezones = (lat, lon) => {
    const venueTimezone = tzLookup(lat, lon);

    setVenueTimezone(venueTimezone);
  };

  const [state, setState] = useState({
    playlist: [],
    rows: [],
    error: null,
  });
  const [venueTimezone, setVenueTimezone] = useState("");

  useEffect(() => {
    if (isLibraryEditor) {
      return;
    }

    if (!props.venueId) {
      const fallbackTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setVenueTimezone(fallbackTimezone);
      return;
    }

    (async () => {
      try {
        const venueItem = await getVenue(props.venueId);
        if (venueItem?.coords?.latitude && venueItem?.coords?.longitude) {
          setTimezones(venueItem.coords.latitude, venueItem.coords.longitude);
          return;
        }
      } catch (error) {
        console.error("Failed to fetch venue timezone for playlist modal.", error);
      }

      const fallbackTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setVenueTimezone(fallbackTimezone);
    })();
  }, [isLibraryEditor, props.venueId]);

  useEffect(() => {
    // This effect initializes the main playlist state from props

    // Determine the playlist's default price from props, stripped of currency symbol.
    // This will be the benchmark for deciding if a song's price is an override or using the default.
    const defaultPriceFromPropsStripped = normalizePriceInput(
      props.playlist?.defaultPrice || ""
    );
    // Consider "0" or empty string as "Free/0" for default price comparison.
    const isDefaultPriceEffectivelyFree =
      defaultPriceFromPropsStripped === "" ||
      defaultPriceFromPropsStripped === "0";

    let initialSongs = [];
    if (props.playlist && Array.isArray(props.playlist.playlist)) {
      initialSongs = props.playlist.playlist.map((p) => {
        const songPriceFromDb = p.price; // e.g., "$5" or undefined (if song was Free/0 when saved)
        const songPriceStripped = songPriceFromDb
          ? normalizePriceInput(songPriceFromDb)
          : undefined; // e.g., "5" or undefined

        let finalSpecificPrice;

        if (songPriceFromDb === undefined) {
          finalSpecificPrice = undefined;
        } else if (songPriceStripped === "0") {
          if (isDefaultPriceEffectivelyFree) {
            // Both song and default are Free/0. Song uses default.
            finalSpecificPrice = undefined;
          } else {
            finalSpecificPrice = "0";
          }
        } else {
          // Song has a non-Free/0 price.
          if (
            !isDefaultPriceEffectivelyFree &&
            songPriceStripped === defaultPriceFromPropsStripped
          ) {
            // Both song and default are the same non-Free/0 price. Song uses default.
            finalSpecificPrice = undefined;
          } else {
            // Song's non-Free/0 price is different from default, or default is Free/0.
            // This is a true specific override.
            finalSpecificPrice = songPriceStripped;
          }
        }
        return { id: newId(), item: p.item, specificPrice: finalSpecificPrice };
      });
    } else if (!props.playlist) {
      initialSongs = [];
    }

    let startTime =
      props.playlist?.startTime &&
      new Date(
        props.playlist.startTime -
          new Date(props.playlist.startTime).getTimezoneOffset() * 60000
      )
        .toISOString()
        .slice(11, 16);
    let endTime =
      props.playlist?.endTime &&
      new Date(
        props.playlist.endTime -
          new Date(props.playlist.endTime).getTimezoneOffset() * 60000
      )
        .toISOString()
        .slice(11, 16);

    if (venueTimezone && props.eventDate) {
      const startDate = new Date(`${props.eventDate}T${startTime || "00:00"}`);
      if (startTime && startTime < "12:00")
        startDate.setDate(startDate.getDate() + 1);
      if (startTime)
        startTime = getTimeInSelectedTimezone(startDate, venueTimezone);

      const endDate = new Date(`${props.eventDate}T${endTime || "00:00"}`);
      if (endTime && endTime <= "12:00") endDate.setDate(endDate.getDate() + 1);
      if (endTime) endTime = getTimeInSelectedTimezone(endDate, venueTimezone);
    }

    const defaultEventDate = new Date().toISOString().slice(0, 10);

    setState((s) => ({
      ...s,
      ...(props.playlist || {}),
      name: props.playlist?.name || s.name || "",
      addToLibrary: false,
      playlist: initialSongs,
      defaultPrice:
        defaultPriceFromPropsStripped === "0" &&
        (props.playlist?.defaultPrice || "") === `${currencySymbol}0`
          ? "0"
          : defaultPriceFromPropsStripped,
      suggestionsPrice: normalizePriceInput(props.playlist?.suggestionsPrice || ""),
      startTime: startTime || s.startTime || "20:00",
      endTime: endTime || s.endTime || "23:00",
      venueId: props.venueId,
      eventId: props.eventId,
      eventDate: props.eventDate || defaultEventDate,
    }));
  }, [
    props.playlist,
    props.venueId,
    props.eventId,
    props.eventDate,
    currencySymbol,
    venueTimezone,
  ]);

  const handleImport = async (e) => {
    const playlistFile = e.target.files[0];
    if (!playlistFile) return;

    setState((s) => ({
      ...s,
      isImporting: true,
    }));

    try {
      const base64Playlist = await readStreamAsBase64(playlistFile);
      const parsedSongs = await previewPlaylist({ base64: base64Playlist });

      setState((s) => {
        const newSongsFromImport = Array.isArray(parsedSongs)
          ? parsedSongs.map((item) => ({
              id: newId(),
              item: item,
              specificPrice: undefined,
            }))
          : [];

        const combinedPlaylist = [...(s.playlist || []), ...newSongsFromImport];

        const uniquePlaylistItems = [];
        const seenNamedSongs = new Set();
        combinedPlaylist.forEach((pItem) => {
          if (pItem.item !== "") {
            // For named songs
            if (!seenNamedSongs.has(pItem.item)) {
              uniquePlaylistItems.push(pItem);
              seenNamedSongs.add(pItem.item);
            }
          } else {
            // For songs with item: "" (typically new, manually added ones)
            uniquePlaylistItems.push(pItem); // Keep all of them as they have unique IDs
          }
        });

        return {
          ...s,
          playlist: uniquePlaylistItems,
          isImporting: false,
        };
      });
    } catch (error) {
      console.error("Error importing playlist:", error);
      setState((s) => ({
        ...s,
        isImporting: false,
        error: "Failed to import playlist. Please try again.",
      }));
    }

    e.target.value = null;
  };

  useEffect(() => {
    setState((s) => ({
      ...s,
      rows: Array.isArray(s.playlist)
        ? s.playlist.map((pItem) => ({
            id: pItem.id,
            item: pItem.item,
            price: pItem.specificPrice,
          }))
        : [],
    }));
  }, [state.playlist, currencySymbol]);

  const handleCopyConfirm = (playlistFromCopyModal, isAppend) => {
    setState((prevState) => {
      const songsToAdd = playlistFromCopyModal.map((song) => ({
        id: newId(),
        item: song.item,
        specificPrice: undefined,
      }));

      let newCombinedPlaylist;
      if (isAppend) {
        newCombinedPlaylist = [...(prevState.playlist || []), ...songsToAdd];
      } else {
        newCombinedPlaylist = songsToAdd;
      }

      const uniquePlaylistItems = [];
      const seenNamedSongs = new Set();
      newCombinedPlaylist.forEach((pItem) => {
        if (pItem.item !== "") {
          // For named songs
          if (!seenNamedSongs.has(pItem.item)) {
            uniquePlaylistItems.push(pItem);
            seenNamedSongs.add(pItem.item);
          }
        } else {
          // For songs with item: "" (typically new, manually added ones)
          uniquePlaylistItems.push(pItem); // Keep all of them as they have unique IDs
        }
      });

      return {
        ...prevState,
        playlist: uniquePlaylistItems,
        openCopyModal: false,
      };
    });
  };

  const handleCopy = () => {
    if (isLibraryEditor) {
      setState((s) => ({
        ...s,
        openCopyModal: true,
      }));
      return;
    }

    setState((s) => ({
      ...s,
      openLibrarySelectionModal: true,
    }));
  };

  const handleClose = () => {
    setState((s) => ({
      ...s,
      openCopyModal: false,
    }));
  };

  const handleCloseLibrarySelectionModal = () => {
    setState((s) => ({
      ...s,
      openLibrarySelectionModal: false,
    }));
  };

  const handleLibrarySelect = (libraryPlaylist) => {
    const songsToAdd = (libraryPlaylist?.playlist || []).map((song) => ({
      id: newId(),
      item: song.item,
      specificPrice: song?.price ? normalizePriceInput(song.price) : undefined,
    }));

    const uniquePlaylistItems = [];
    const seenNamedSongs = new Set();
    songsToAdd.forEach((pItem) => {
      if (pItem.item !== "") {
        if (!seenNamedSongs.has(pItem.item)) {
          uniquePlaylistItems.push(pItem);
          seenNamedSongs.add(pItem.item);
        }
      } else {
        uniquePlaylistItems.push(pItem);
      }
    });

    setState((s) => ({
      ...s,
      openLibrarySelectionModal: false,
      playlist: uniquePlaylistItems,
      name: s.name || libraryPlaylist?.name || "",
    }));
  };

  const handleClear = () => {
    setState((s) => ({
      ...s,
      playlist: [],
    }));
  };

  const handleConfirm = async () => {
    setState((s) => ({ ...s, error: null }));
    const errors = {};
    if (props.showNameField && !String(state.name || "").trim()) {
      errors.name = "Playlist name is required.";
    }
    if (!isLibraryEditor) {
      if (!state.startTime) {
        errors.startTime = "Start Time is required.";
      }
      if (!state.endTime) {
        errors.endTime = "End Time is required.";
      }
      if (
        state.maxOrdersPerPlaylist === undefined ||
        state.maxOrdersPerPlaylist === null ||
        String(state.maxOrdersPerPlaylist).trim() === ""
      ) {
        errors.maxOrdersPerPlaylist = "Required.";
      }
      if (
        state.defaultPrice === undefined ||
        state.defaultPrice === null ||
        String(state.defaultPrice).trim() === ""
      ) {
        errors.defaultPrice = "Required.";
      }
      if (
        state.hasSuggestions &&
        (state.suggestionsPrice === undefined ||
          state.suggestionsPrice === null ||
          String(state.suggestionsPrice).trim() === "")
      ) {
        errors.suggestionsPrice = "Required when custom requests are enabled.";
      }
    }

    if (Object.keys(errors).length > 0) {
      setState((s) => ({ ...s, errors, isLoading: false }));
      return;
    }

    setState((s) => ({
      ...s,
      isLoading: true,
      errors: undefined,
    }));

    const mappedPlaylist = state.playlist?.map((pItem) => {
      const hasSpecificPrice =
        pItem.specificPrice !== undefined &&
        pItem.specificPrice !== null &&
        pItem.specificPrice !== "";
      const priceToSave = hasSpecificPrice
        ? normalizePriceInput(pItem.specificPrice)
        : undefined;
      const isNumeric =
        priceToSave !== undefined &&
        priceToSave !== "" &&
        !isNaN(parseFloat(priceToSave));
      return {
        item: pItem.item,
        price: isNumeric ? `${currencySymbol}${priceToSave}` : undefined,
      };
    });

    if (isLibraryEditor) {
      const result = await props.handleConfirm({
        id: state.id,
        name: String(state.name || "").trim(),
        playlist: mappedPlaylist,
      });

      if (typeof result === "string") {
        setState((s) => ({ ...s, error: result, isLoading: false }));
      } else {
        props.handleClose();
      }
      return;
    }

    const effectiveEventDate =
      state.eventDate || new Date().toISOString().slice(0, 10);
    const startDate = new Date(`${effectiveEventDate}T${state.startTime}`);
    if (state.startTime < "12:00") {
      startDate.setDate(startDate.getDate() + 1);
    }
    const startTimeInVenueTimezone = zonedTimeToUtc(
      startDate,
      venueTimezone
    ).getTime();

    const endDate = new Date(`${effectiveEventDate}T${state.endTime}`);
    if (state.endTime <= "12:00") {
      endDate.setDate(endDate.getDate() + 1);
    }
    const endTimeInvenueTimezone = zonedTimeToUtc(
      endDate,
      venueTimezone
    ).getTime();

    const defaultPriceValue = normalizePriceInput(state.defaultPrice);
    const isValidDefaultPrice =
      defaultPriceValue !== "" &&
      defaultPriceValue !== null &&
      defaultPriceValue !== undefined &&
      !isNaN(parseFloat(defaultPriceValue));

    const body = {
      id: state.id,
      name: String(state.name || "").trim(),
      addToLibrary: !!state.addToLibrary,
      startTime: startTimeInVenueTimezone,
      endTime: endTimeInvenueTimezone,
      venueId: state.venueId,
      eventId: state.eventId,
      playlist: mappedPlaylist,
      price: isValidDefaultPrice // Obsolete field, but keep logic consistent
        ? `${currencySymbol}${defaultPriceValue}`
        : undefined,
      defaultPrice: isValidDefaultPrice // Correctly handles "0"
        ? `${currencySymbol}${defaultPriceValue}`
        : undefined,
      maxOrdersPerPlaylist: parseInt(state.maxOrdersPerPlaylist) || 0,
      maxPaidOrdersPerPlaylist: parseInt(state.maxPaidOrdersPerPlaylist),
      hasSuggestions: state.hasSuggestions,
      suggestionsPrice: parseFloat(normalizePriceInput(state.suggestionsPrice))
        ? `${currencySymbol}${normalizePriceInput(state.suggestionsPrice)}`
        : undefined,
    };
    const result = await props.handleConfirm(body);

    if (typeof result === "string") {
      setState((s) => ({ ...s, error: result, isLoading: false }));
    } else {
      props.handleClose();
    }
  };

  const handleChange = (propName) => (event) => {
    if (event.target.type === "number") {
      const value = event.target.value;

      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) {
        setState((s) => ({
          ...s,
          [propName]: "",
        }));
        return;
      }
    }

    setState((s) => ({
      ...s,
      [propName]: event.target.value,
    }));
  };

  const preventNegativeInput = (e) => {
    if (e.key === "-" || e.key === "e") {
      e.preventDefault();
    }
  };

  const handleHasSuggestionsChange = (e) => {
    setState((s) => ({
      ...s,
      hasSuggestions: e.target.checked,
    }));
  };

  const handleAddToLibraryChange = (e) => {
    setState((s) => ({
      ...s,
      addToLibrary: e.target.checked,
    }));
  };

  const handleStartTimeChange = (e, value) => {
    setState((s) => {
      const startTime = value.value;
      return {
        ...s,
        startTime,
      };
    });
  };

  const handleEndTimeChange = (e, value) => {
    setState((s) => {
      const endTime = value.value;
      return {
        ...s,
        endTime,
      };
    });
  };

  const handleSelectionModelChange = (newSelection) => {
    setSelectedRows(newSelection);
  };

  const handleDeleteSelected = () => {
    setState((s) => ({
      ...s,
      playlist: s.playlist.filter((pItem) => !selectedRows.includes(pItem.id)),
    }));
    setSelectedRows([]);
  };

  const handleAddNewRow = (uniqueIndex = 0) => {
    const newPlaylistItem = {
      id: newId(),
      item: "",
      specificPrice: undefined,
    };
    setState((s) => ({
      ...s,
      playlist: [newPlaylistItem, ...(s.playlist || [])],
    }));
  };

  const handleTableContextMenu = (event) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 12,
            mouseY: event.clientY + 12,
          }
        : null
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setAddMenuAnchorEl(null);
  };

  const handleContextAddOpen = (event) => {
    setAddMenuAnchorEl(event.currentTarget);
  };

  const handleContextAddClose = () => {
    setAddMenuAnchorEl(null);
  };

  const handleAddRows = (count) => {
    for (let i = 0; i < count; i++) {
      handleAddNewRow(i);
    }
    setAddMenuAnchorEl(null);
    handleCloseContextMenu();
  };

  const handlePriceChangeClick = () => {
    setIsPriceChangeModalOpen(true);
  };

  const handlePriceChangeClose = () => {
    setIsPriceChangeModalOpen(false);
  };

  const handlePriceChange = (selectedRowIds, newPrice) => {
    setState((s) => {
      const updatedPlaylist = s.playlist.map((pItem) => {
        if (selectedRowIds.includes(pItem.id)) {
          // If newPrice is 0, "0", "Free", or empty, set specificPrice to undefined so defaultPrice applies.
          // Otherwise, store the new price as a string.
          const newSpecificPrice =
            newPrice === 0 ||
            newPrice === "0" ||
            newPrice === "Free" ||
            newPrice === "" ||
            newPrice === null ||
            newPrice === undefined
              ? undefined
              : newPrice.toString();
          return {
            ...pItem,
            specificPrice: newSpecificPrice,
          };
        }
        return pItem;
      });
      return { ...s, playlist: updatedPlaylist };
    });
  };

  return (
    <>
      {
        <Dialog
          open={true}
          PaperProps={{
            style: {
              backgroundColor: "#d8c8f4",
              color: "#4b445b",
              width: "90%",
              maxWidth: "1600px",
              height: "90vh",
              margin: "20px",
            },
          }}
        >
          <DialogTitle>
            {props.title || (state.id ? "Edit Playlist" : "Create Playlist")}
          </DialogTitle>
          {isLibraryEditor || venueTimezone.length ? (
            <>
              <DialogContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  overflowY: "auto",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isLibraryEditor
                      ? "column"
                      : isMobile
                      ? "column"
                      : "row",
                    flexGrow: 1,
                    gap: 1,
                    alignItems: isMobile ? "stretch" : "flex-start",
                  }}
                >
                  {!isLibraryEditor && (
                    <Box
                      sx={{
                        width: isMobile ? "100%" : "350px",
                        minWidth: isMobile ? "100%" : "300px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        overflowY: isMobile ? "visible" : "auto",
                      }}
                    >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {props.showNameField && (
                        <TextField
                          autoComplete="off"
                          margin="dense"
                          id="name"
                          label={props.nameLabel || "Playlist Name"}
                          placeholder={props.namePlaceholder || ""}
                          type="text"
                          fullWidth
                          variant="outlined"
                          value={state.name || ""}
                          onChange={handleChange("name")}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          required
                          error={!!state.errors?.name}
                          helperText={state.errors?.name}
                          size="medium"
                        />
                      )}
                      <TextField
                        label="Timezone"
                        variant="outlined"
                        value={venueTimezone || ""}
                        disabled
                        fullWidth
                        margin="dense"
                        size="medium"
                      />
                      <Box
                        sx={{ display: "flex", flexDirection: "row", gap: 1 }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Autocomplete
                            disablePortal
                            id="startTime-dropdown"
                            options={timeOptions}
                            value={state.startTime}
                            disableClearable
                            onChange={handleStartTimeChange}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                margin="dense"
                                fullWidth
                                variant="outlined"
                                label="Start Time"
                                id="startTime"
                                type="text"
                                required
                                error={!!state.errors?.startTime}
                                helperText={state.errors?.startTime}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      {params.InputProps.startAdornment}
                                      <InputAdornment position="center">
                                        <AccessTime
                                          sx={{
                                            marginLeft: "4px",
                                            marginRight: "4px",
                                            width: "20px",
                                            height: "20px",
                                          }}
                                        />
                                      </InputAdornment>
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Autocomplete
                            disablePortal
                            id="endTime-dropdown"
                            options={timeOptions}
                            value={state.endTime}
                            disableClearable
                            onChange={handleEndTimeChange}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                margin="dense"
                                fullWidth
                                variant="outlined"
                                label="End Time"
                                id="endTime"
                                type="text"
                                required
                                error={!!state.errors?.endTime}
                                helperText={state.errors?.endTime}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      {params.InputProps.startAdornment}
                                      <InputAdornment position="center">
                                        <AccessTime
                                          sx={{
                                            marginLeft: "4px",
                                            marginRight: "4px",
                                            width: "20px",
                                            height: "20px",
                                          }}
                                        />
                                      </InputAdornment>
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <TextField
                        autoComplete="off"
                        margin="dense"
                        id="maxOrdersPerPlaylist"
                        label="Maximum number of free requests"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={state.maxOrdersPerPlaylist}
                        onChange={handleChange("maxOrdersPerPlaylist")}
                        onKeyDown={preventNegativeInput}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{ min: 0 }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        required
                        error={!!state.errors?.maxOrdersPerPlaylist}
                        helperText={state.errors?.maxOrdersPerPlaylist}
                        size="medium"
                      />
                      <TextField
                        autoComplete="off"
                        margin="dense"
                        id="defaultPrice"
                        label="Default price for requests"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={state.defaultPrice}
                        onChange={handleChange("defaultPrice")}
                        onKeyDown={preventNegativeInput}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {currency === "EUR" && (
                                <Euro sx={{ width: "20px", height: "20px" }} />
                              )}
                              {currency === "USD" && (
                                <Dollar
                                  sx={{
                                    width: "20px",
                                    height: "20px",
                                  }}
                                />
                              )}
                            </InputAdornment>
                          ),
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        required
                        error={!!state.errors?.defaultPrice}
                        helperText={state.errors?.defaultPrice}
                        size="medium"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={state.hasSuggestions}
                            onChange={handleHasSuggestionsChange}
                          />
                        }
                        label="Custom Requests"
                        key="hasSuggestions"
                        sx={{ mt: -0.5, mb: -0.5 }}
                      />
                      <TextField
                        autoComplete="off"
                        margin="dense"
                        id="suggestionsPrice"
                        label="Price for custom requests"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={state.suggestionsPrice}
                        onChange={handleChange("suggestionsPrice")}
                        onKeyDown={preventNegativeInput}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {currency === "EUR" && (
                                <Euro sx={{ width: "20px", height: "20px" }} />
                              )}
                              {currency === "USD" && (
                                <Dollar
                                  sx={{
                                    width: "20px",
                                    height: "20px",
                                  }}
                                />
                              )}
                            </InputAdornment>
                          ),
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        required={state.hasSuggestions}
                        error={!!state.errors?.suggestionsPrice}
                        helperText={state.errors?.suggestionsPrice}
                        disabled={!state.hasSuggestions}
                        size="medium"
                      />
                    </Box>
                    </Box>
                  )}

                  <Box
                    sx={{
                      flexGrow: 1,
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      height: isMobile ? "auto" : "100%",
                      minHeight: isMobile ? 500 : "auto",
                    }}
                  >
                    {isLibraryEditor && props.showNameField && (
                      <TextField
                        autoComplete="off"
                        margin="dense"
                        id="name"
                        label={props.nameLabel || "Playlist Name"}
                        placeholder={props.namePlaceholder || ""}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={state.name || ""}
                        onChange={handleChange("name")}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        required
                        error={!!state.errors?.name}
                        helperText={state.errors?.name}
                        size="medium"
                      />
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexDirection: isMobile ? "column" : "row",
                          width: isMobile ? "100%" : "auto",
                          "& > *": {
                            width: isMobile ? "100%" : "auto",
                          },
                        }}
                      >
                        <Button
                          variant="outlined"
                          component="label"
                          sx={{ fontSize: "0.65rem" }}
                          disabled={state.isImporting}
                        >
                          <UploadFileIcon
                            sx={{
                              fontSize: "20px",
                              marginRight: "4px",
                            }}
                          />
                          {state.isImporting
                            ? "Importing..."
                            : "Import playlist from file"}
                          <input
                            type="file"
                            hidden
                            onChange={handleImport}
                            accept=".crate,.m3u,.m3u8,.txt"
                            disabled={state.isImporting}
                          />
                        </Button>
                        <Button
                          variant="outlined"
                          component="label"
                          onClick={handleCopy}
                          sx={{ fontSize: "0.65rem" }}
                        >
                          <FileCopyIcon
                            sx={{
                              fontSize: "20px",
                              marginRight: "4px",
                            }}
                          />
                          {isLibraryEditor
                            ? "Copy playlist from past events"
                            : "Playlist from Library"}
                        </Button>
                        <Button
                          variant="outlined"
                          component="label"
                          onClick={handleClear}
                          sx={{ fontSize: "0.65rem" }}
                        >
                          <HighlightOffIcon
                            sx={{
                              fontSize: "20px",
                              marginRight: "4px",
                            }}
                          />
                          Delete Current Playlist
                        </Button>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          width: isMobile ? "100%" : "auto",
                          justifyContent: isMobile
                            ? "space-between"
                            : "flex-end",
                          marginTop: isMobile ? 2 : 0,
                        }}
                      >
                        <Tooltip title="Add song" arrow>
                          <IconButton
                            onClick={handleAddNewRow}
                            sx={{
                              color: "primary.main",
                              "&:hover": {
                                backgroundColor: "rgba(25, 118, 210, 0.04)",
                              },
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Change price for selected songs" arrow>
                          <span>
                            <IconButton
                              onClick={handlePriceChangeClick}
                              sx={{
                                color: "primary.main",
                                "&:hover": {
                                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                                },
                              }}
                              disabled={selectedRows.length === 0}
                            >
                              {currency === "EUR" ? <Euro /> : <Dollar />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete selected song" arrow>
                          <span>
                            <IconButton
                              onClick={handleDeleteSelected}
                              sx={{
                                color: "error.main",
                                "&:hover": {
                                  backgroundColor: "rgba(211, 47, 47, 0.04)",
                                },
                              }}
                              disabled={selectedRows.length === 0}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {props.onVibyJingleDownload && (
                          <Tooltip title="Download Viby Jingle" arrow>
                            <IconButton
                              onClick={props.onVibyJingleDownload}
                              sx={{
                                color: "#cf1b88",
                                "&:hover": {
                                  backgroundColor: "rgba(207, 27, 136, 0.04)",
                                },
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                        "& .MuiDataGrid-root": {
                          border: "none",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: "#f5f5f5",
                        },
                        "& .MuiDataGrid-virtualScroller": {
                          overflow: "auto",
                        },
                        "& .price-cell": {
                          borderLeft: "1px solid #e0e0e0",
                        },
                        "& .price-cell.Mui-selected": {
                          borderLeft: "2px solid #1976d2",
                        },
                      }}
                      onContextMenu={handleTableContextMenu}
                    >
                      {state.isImporting ? (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                            gap: 1,
                          }}
                        >
                          <Loader />
                          <Typography variant="body2" color="textSecondary">
                            Importing playlist...
                          </Typography>
                        </Box>
                      ) : null}

                      <DataGrid
                        rows={state.rows}
                        columns={columns}
                        onCellEditCommit={handleCellEditCommit}
                        onSortModelChange={handleSortModelChange}
                        checkboxSelection
                        disableSelectionOnClick
                        onSelectionModelChange={handleSelectionModelChange}
                        selectionModel={selectedRows}
                        hideFooter={state.isImporting}
                        slots={{
                          noRowsOverlay: () =>
                            !state.isImporting ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  height: "100%",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  No songs in playlist
                                </Typography>
                              </Box>
                            ) : (
                              <div style={{ display: "none" }} />
                            ),
                        }}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          borderColor: "primary.main",
                          "& .MuiDataGrid-overlay": {
                            display: state.isImporting ? "none" : "flex",
                          },
                          "& .MuiDataGrid-columnHeaders": {
                            borderColor: "primary.main",
                          },
                          "& .MuiDataGrid-columnHeaders:hover": {
                            color: "primary.main",
                          },
                          "& .MuiDataGrid-columnSeparator": {
                            color: "primary.main",
                          },
                          "& .MuiDataGrid-cell": {
                            borderColor: "primary.main",
                          },
                          "& .MuiDataGrid-cell:hover": {
                            color: "primary.main",
                          },
                        }}
                      />
                      <Menu
                        open={contextMenu !== null}
                        onClose={handleCloseContextMenu}
                        anchorReference="anchorPosition"
                        anchorPosition={
                          contextMenu !== null
                            ? {
                                top: contextMenu.mouseY,
                                left: contextMenu.mouseX,
                              }
                            : undefined
                        }
                      >
                        <MenuItem
                          onClick={handleContextAddOpen}
                          aria-haspopup="true"
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              marginRight: "10px",
                              color: "#d8c8f4",
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Add songs</ListItemText>
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              marginLeft: "auto",
                              color: "#d8c8f4",
                            }}
                          >
                            <ArrowRightIcon fontSize="small" />
                          </ListItemIcon>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            handlePriceChangeClick();
                            handleCloseContextMenu();
                          }}
                          disabled={selectedRows.length === 0}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              marginRight: "10px",
                              color: "#d8c8f4",
                            }}
                          >
                            {currency === "EUR" ? (
                              <Euro fontSize="small" />
                            ) : (
                              <Dollar fontSize="small" />
                            )}
                          </ListItemIcon>
                          <ListItemText>Change Price</ListItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            handleDeleteSelected();
                            handleCloseContextMenu();
                          }}
                          disabled={selectedRows.length === 0}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              marginRight: "10px",
                              color: "#d8c8f4",
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Delete Selected Songs</ListItemText>
                        </MenuItem>
                      </Menu>
                      <Menu
                        anchorEl={addMenuAnchorEl}
                        open={Boolean(addMenuAnchorEl)}
                        onClose={handleContextAddClose}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                      >
                        <MenuItem onClick={() => handleAddRows(1)}>
                          1 Song
                        </MenuItem>
                        <MenuItem onClick={() => handleAddRows(5)}>
                          5 Songs
                        </MenuItem>
                        <MenuItem onClick={() => handleAddRows(10)}>
                          10 Songs
                        </MenuItem>
                      </Menu>
                    </Box>
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions>
                {state.error && (
                  <Typography
                    color="error"
                    sx={{
                      width: "100%",
                      textAlign: "left",
                      marginLeft: "10px",
                    }}
                  >
                    {state.error}
                  </Typography>
                )}
                {props.showAddToLibraryCheckbox && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!state.addToLibrary}
                        onChange={handleAddToLibraryChange}
                      />
                    }
                    label="Add to Library"
                    key="addToLibraryFooter"
                    sx={{ mr: 1 }}
                  />
                )}
                <Button onClick={props.handleClose} variant="contained">
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirm}
                  variant="contained"
                  disabled={state.isLoading}
                >
                  Confirm
                </Button>
              </DialogActions>
            </>
          ) : (
            <Loader isModal="true" />
          )}
        </Dialog>
      }
      {state.openCopyModal && (
        <CopyPlaylistModal
          playlistId={state.id}
          eventId={props.eventId}
          venueId={props.venueId}
          handleConfirm={handleCopyConfirm}
          handleClose={handleClose}
        />
      )}
      {state.openLibrarySelectionModal && (
        <LibrarySelectionModal
          handleSelect={handleLibrarySelect}
          handleClose={handleCloseLibrarySelectionModal}
        />
      )}
      <PriceChangeModal
        open={isPriceChangeModalOpen}
        onClose={handlePriceChangeClose}
        selectedRows={selectedRows}
        rows={state.rows || []}
        onPriceChange={handlePriceChange}
        currencySymbol={currencySymbol}
        currency={currency}
      />
    </>
  );
}
